import API from 'utils/api';
import getFormattedValues, { hasFiles } from 'utils/formatValues';
import { onSuccess, onError } from 'utils/handlers';

export const baseURI = 'usuarios/usuarios';
export const baseCustomURI = 'usuarios/usuarios-custom';

const getValuesAndConfig = (values) => {
  const files = ['foto', 'firma_electronica'];
  const config = hasFiles(values, files) ? {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  } : {};
  const formattedValues = getFormattedValues(values, {
    files,
    formData: config.headers,
    clean: false,
  });
  return [formattedValues, config];
};
/**
 * GET Usuarios
 * @returns {Promise<object[]>}
 */
export const getUsuarios = async () => {
  try {
    const response = await API.get(baseURI);
    return response.data;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Usuario
 * @param {number} id
 * @returns {Promise<object|null>}
 */
export const getUsuario = async (id) => {
  try {
    const response = await API.get(`${baseURI}/${id}`);
    return response.data;
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * POST Usuario
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const postUsuario = async (values) => {
  try {
    const response = await API.post(baseURI, ...getValuesAndConfig(values));
    onSuccess(response, 'Agregado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Usuario
 * @param {number} id
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const patchUsuario = async (id, values) => {
  try {
    const response = await API.patch(`${baseURI}/${id}`, ...getValuesAndConfig(values));
    onSuccess(response, 'Editado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * DELETE Usuario
 * @param {number} id
 * @returns {Promise<boolean>}
 */
export const deleteUsuario = async (id) => {
  try {
    const response = await API.delete(`${baseURI}/${id}`);
    onSuccess(response, 'Eliminado correctamente');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Usuario — lanza el error en lugar de capturarlo, para que el
 * componente pueda mostrarlo en los campos del formulario.
 * @param {number} id
 * @param {object} values
 * @returns {Promise<object>}
 */
export const patchUsuarioThrow = async (id, values) => {
  const [formattedValues, config] = getValuesAndConfig(values);
  const response = await API.patch(`${baseURI}/${id}`, formattedValues, config);
  onSuccess(response, 'Editado correctamente');
  return response.data;
};

/**
 * POST Usuario — lanza el error en lugar de capturarlo, para que el
 * componente pueda mostrarlo en los campos del formulario.
 * @param {object} values
 * @returns {Promise<object>}
 */
export const postUsuarioThrow = async (values) => {
  const [formattedValues, config] = getValuesAndConfig(values);
  const response = await API.post(baseURI, formattedValues, config);
  onSuccess(response, 'Agregado correctamente');
  return response.data;
};

/**
 * POST Generar Usuarios Desde Empleados — lanza el proceso Celery en background.
 * Regresa el response completo (sin capturar el error) para que el componente
 * pueda inspeccionar el status: 202 = proceso lanzado (activar polling),
 * 400 = configuración de la entidad no habilitada, 409 = ya hay un proceso en curso.
 * @returns {Promise<object|boolean>}
 */
export const postGenerarUsuariosDesdeEmpleados = async () => {
  try {
    const response = await API.post(`${baseURI}/generar-desde-empleados/`);
    return response;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Usuarios Custom
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getUsuariosCustom = async (params) => {
  try {
    const response = await API.get(baseCustomURI, { params });
    return response.data;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * POST UsuarioCustom
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const postUsuarioCustom = async (values) => {
  try {
    const response = await API.post(baseCustomURI, ...getValuesAndConfig(values));
    onSuccess(response, 'Agregado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH UsuarioCustom
 * @param {number} id
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const patchUsuarioCustom = async (id, values) => {
  try {
    const response = await API.patch(`${baseCustomURI}/${id}`, ...getValuesAndConfig(values));
    onSuccess(response, 'Editado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};
